/**
 * AI Assessment Modal Component
 * 
 * Conversational UI for AI-powered skill assessments.
 * Features:
 * - 4-round conversational assessment flow
 * - Real-time progress tracking
 * - Keyboard shortcuts (Enter to submit, Shift+Enter for newline)
 * - Auto-scroll conversation
 * - Loading states and error handling
 * - Results display with feedback and learning resources
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, Loader2, CheckCircle, XCircle, TrendingUp, BookOpen, AlertCircle } from 'lucide-react';
import aiSkillsAssessmentService from '../../../services/aiSkillsAssessmentService';
import { useAuth } from '../../../hooks/useAuth';

const AIAssessmentModal = ({ isOpen, onClose, skill, category, onAssessmentComplete }) => {
  const { user } = useAuth();
  
  // State management
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [userResponse, setUserResponse] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [roundNumber, setRoundNumber] = useState(0);
  const [isStarting, setIsStarting] = useState(true);
  const [evaluatingAnswer, setEvaluatingAnswer] = useState(false);
  const [pendingResponse, setPendingResponse] = useState(null);
  
  // Refs
  const textareaRef = useRef(null);
  const conversationEndRef = useRef(null);
  const conversationContainerRef = useRef(null);

  // Auto-scroll to bottom of conversation
  const scrollToBottom = () => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversationHistory, currentQuestion]);

  // Initialize assessment when modal opens
  useEffect(() => {
    if (isOpen && !sessionId && isStarting) {
      startNewAssessment();
    }
  }, [isOpen]);

  // Focus textarea when question appears
  useEffect(() => {
    if (currentQuestion && !loading && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [currentQuestion, loading]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const resetState = () => {
    setSessionId(null);
    setCurrentQuestion('');
    setUserResponse('');
    setConversationHistory([]);
    setLoading(false);
    setError(null);
    setProgress(0);
    setResult(null);
    setRoundNumber(0);
    setIsStarting(true);
    setEvaluatingAnswer(false);
    setPendingResponse(null);
  };

  const startNewAssessment = async () => {
    setLoading(true);
    setError(null);
    setIsStarting(false);
    
    try {
      console.log('[AIAssessmentModal] Starting assessment for:', skill.name || skill);
      const response = await aiSkillsAssessmentService.startAssessment(
        user.id,
        skill.name || skill
      );
      
      console.log('[AIAssessmentModal] Assessment started, response:', response);
      console.log('[AIAssessmentModal] Question received:', response.question);
      
      setSessionId(response.sessionId);
      setCurrentQuestion(response.question);
      setProgress(response.progress);
      setRoundNumber(response.roundNumber);
      
      // Add first question to conversation history
      setConversationHistory([{
        type: 'ai',
        content: response.question,
        round: response.roundNumber
      }]);
      
      console.log('[AIAssessmentModal] State updated, currentQuestion:', response.question);
    } catch (err) {
      console.error('[AIAssessmentModal] Start assessment error:', err);
      setError(err.message || 'Failed to start assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitResponse = async () => {
    if (!userResponse.trim() || loading) return;
    
    const submittedResponse = userResponse;
    setUserResponse('');
    setEvaluatingAnswer(true);
    setPendingResponse(submittedResponse);
    setLoading(true);
    setError(null);
    
    // Add user response to conversation history
    const newHistory = [
      ...conversationHistory,
      {
        type: 'user',
        content: submittedResponse,
        round: roundNumber
      }
    ];
    setConversationHistory(newHistory);
    
    await evaluateAnswer(sessionId, submittedResponse, newHistory);
  };

  const evaluateAnswer = async (sessionId, submittedResponse, newHistory) => {
    try {
      const response = await aiSkillsAssessmentService.submitResponse(
        sessionId,
        submittedResponse
      );
      
      if (response.completed) {
        // Assessment finished
        setResult(response.result);
        setProgress(1);
        setEvaluatingAnswer(false);
        setPendingResponse(null);
        
        // Notify parent component
        if (onAssessmentComplete) {
          onAssessmentComplete(response.result);
        }
      } else {
        // Add next question to conversation
        setConversationHistory([
          ...newHistory,
          {
            type: 'ai',
            content: response.nextQuestion,
            round: response.roundNumber
          }
        ]);
        setCurrentQuestion(response.nextQuestion);
        setProgress(response.progress);
        setRoundNumber(response.roundNumber);
        setEvaluatingAnswer(false);
        setPendingResponse(null);
      }
    } catch (err) {
      console.error('[AIAssessmentModal] Submit response error:', err);
      setError(err.message || 'Failed to evaluate response. Click "Re-evaluate" to retry.');
      // Keep pending response for retry
    } finally {
      setLoading(false);
    }
  };

  const handleReEvaluate = async () => {
    if (!pendingResponse || !sessionId) return;
    
    setError(null);
    setLoading(true);
    setEvaluatingAnswer(true);
    
    // Get current conversation history (should have user response)
    const currentHistory = conversationHistory;
    
    await evaluateAnswer(sessionId, pendingResponse, currentHistory);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitResponse();
    }
  };

  const handleRetry = () => {
    setError(null);
    if (!sessionId) {
      startNewAssessment();
    }
  };

  const handleClose = () => {
    if (result || window.confirm('Are you sure you want to exit? Your progress will be lost.')) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              VA Skills Assessment
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {skill.name || skill} {category ? `• ${category}` : ''}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Progress Bar */}
        {!result && (
          <div className="px-6 pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Progress: Round {roundNumber} of 4
              </span>
              <span className="text-sm text-gray-600">
                {Math.round(progress * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {result ? (
            /* Results View */
            <div className="flex-1 overflow-y-auto p-6">
              <div className="max-w-2xl mx-auto">
                {/* Pass/Fail Status */}
                <div className={`rounded-lg p-6 mb-6 ${
                  result.passed 
                    ? 'bg-green-50 border-2 border-green-200' 
                    : 'bg-amber-50 border-2 border-amber-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {result.passed ? (
                        <CheckCircle className="text-green-600" size={32} />
                      ) : (
                        <XCircle className="text-amber-600" size={32} />
                      )}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">
                          {result.passed ? 'Assessment Passed!' : 'Keep Practicing'}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {result.passed 
                            ? 'Your skill has been verified and added to your profile' 
                            : 'You can retake this assessment in 1 hour'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-gray-900">
                        {result.score}
                      </div>
                      <div className="text-sm text-gray-600">Score</div>
                    </div>
                  </div>
                  
                  {/* Proficiency Level */}
                  <div className="mt-4 flex items-center space-x-2">
                    <TrendingUp size={20} className="text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">
                      Proficiency Level:
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      result.proficiencyLevel === 'expert' ? 'bg-purple-100 text-purple-800' :
                      result.proficiencyLevel === 'advanced' ? 'bg-blue-100 text-blue-800' :
                      result.proficiencyLevel === 'intermediate' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {result.proficiencyLevel.charAt(0).toUpperCase() + result.proficiencyLevel.slice(1)}
                    </span>
                  </div>
                </div>

                {/* Strengths */}
                {result.strengths && result.strengths.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <CheckCircle className="text-green-600 mr-2" size={20} />
                      Strengths
                    </h4>
                    <ul className="space-y-2">
                      {result.strengths.map((strength, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-green-600 mr-2">•</span>
                          <span className="text-gray-700">{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Improvement Areas */}
                {result.improvementAreas && result.improvementAreas.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <TrendingUp className="text-blue-600 mr-2" size={20} />
                      Areas for Improvement
                    </h4>
                    <ul className="space-y-2">
                      {result.improvementAreas.map((area, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-blue-600 mr-2">•</span>
                          <span className="text-gray-700">{area}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Feedback */}
                {result.feedback && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      Detailed Feedback
                    </h4>
                    <p className="text-gray-700 whitespace-pre-line">
                      {result.feedback}
                    </p>
                  </div>
                )}

                {/* Learning Resources */}
                {result.learningResources && result.learningResources.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <BookOpen className="text-purple-600 mr-2" size={20} />
                      Recommended Resources
                    </h4>
                    <div className="space-y-3">
                      {result.learningResources.map((resource, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4">
                          <h5 className="font-medium text-gray-900 mb-1">
                            {resource.title}
                          </h5>
                          {resource.description && (
                            <p className="text-sm text-gray-600 mb-2">
                              {resource.description}
                            </p>
                          )}
                          {resource.url && (
                            <a
                              href={resource.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                              View Resource →
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            /* Conversation View */
            <>
              <div 
                ref={conversationContainerRef}
                className="flex-1 overflow-y-auto p-6 space-y-4"
              >
                {conversationHistory.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-4 ${
                        message.type === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      {message.type === 'ai' && (
                        <div className="text-xs font-semibold mb-2 text-gray-600">
                          Round {message.round}
                        </div>
                      )}
                      <p className="whitespace-pre-line">{message.content}</p>
                    </div>
                  </div>
                ))}
                
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-lg p-4">
                      <Loader2 className="animate-spin text-gray-600" size={24} />
                    </div>
                  </div>
                )}
                
                <div ref={conversationEndRef} />
              </div>

              {/* Error Display */}
              {error && (
                <div className="px-6 pb-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                    <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                    <div className="flex-1">
                      <p className="text-sm text-red-800">{error}</p>
                      <div className="mt-3 flex space-x-2">
                        {pendingResponse && (
                          <button
                            onClick={handleReEvaluate}
                            disabled={loading}
                            className="text-sm font-medium px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 transition-colors"
                          >
                            {loading ? 'Re-evaluating...' : 'Re-evaluate Answer'}
                          </button>
                        )}
                        <button
                          onClick={handleRetry}
                          className="text-sm font-medium text-red-600 hover:text-red-700"
                        >
                          Start Over
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className="border-t border-gray-200 p-6">
                <div className="flex items-end space-x-3">
                  <div className="flex-1">
                    <textarea
                      ref={textareaRef}
                      value={userResponse}
                      onChange={(e) => setUserResponse(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={loading ? "Processing..." : "Type your answer... (Shift+Enter for new line)"}
                      disabled={loading || !currentQuestion}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      rows={3}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Press Enter to submit • Shift+Enter for new line
                    </p>
                  </div>
                  <button
                    onClick={handleSubmitResponse}
                    disabled={loading || !userResponse.trim() || !currentQuestion}
                    className="bg-blue-600 text-white p-4 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex-shrink-0"
                    aria-label="Send"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={24} />
                    ) : (
                      <Send size={24} />
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AIAssessmentModal;
